import type { HelpTopicPair } from "../types";

const topic: HelpTopicPair = {
  th: {
    id: "roles",
    title: "บทบาท",
    summary:
      'บทบาทกำหนดว่าผู้ดูแลแต่ละคนทำอะไรในคอนโซลนี้ได้บ้าง บทบาทหนึ่งคือชุดของ "สิทธิ์" (เช่น ดูผู้ใช้ แก้ไขการตั้งค่า อ่านบันทึกการใช้งาน) หน้านี้แสดงบทบาททั้งหมด และให้สร้าง แก้ไข หรือลบบทบาทที่สร้างเองได้',
    access:
      "เปิดดูได้เมื่อมีสิทธิ์ roles:read (owner, admin, auditor) · สร้าง แก้ไข และลบบทบาทได้เมื่อมีสิทธิ์ roles:write (owner, admin) · การมอบบทบาทให้ผู้ใช้ทำที่หน้ารายละเอียดผู้ใช้ → แท็บ บทบาท (ต้องมีสิทธิ์ roles:write)",
    shot: {
      file: "roles-list.png",
      caption: "หน้ารายการบทบาท: บทบาทที่มีในระบบ 4 บทบาท และบทบาทที่สร้างเอง",
    },
    sections: [
      {
        id: "concept",
        heading: "บทบาทคืออะไร และใครมีได้",
        body: [
          "บทบาทใช้กับคอนโซลผู้ดูแลนี้เท่านั้น ไม่ได้กำหนดว่าผู้ใช้เข้าแอปอื่นได้หรือไม่ (เรื่องนั้นใช้ประเภทผู้ใช้และกลุ่ม) ผู้ใช้หนึ่งคนมีได้หลายบทบาท และได้สิทธิ์รวมของทุกบทบาท",
          "ผู้ที่มีบทบาทซึ่งมีสิทธิ์อย่างน้อยหนึ่งข้อ จึงจะเข้าคอนโซลได้ ผู้ที่ไม่มีบทบาท หรือมีแต่บทบาทที่ไม่มีสิทธิ์ (เช่น staff) จะเข้าคอนโซลไม่ได้",
          "บทบาทคอนโซลให้ได้เฉพาะบัญชีประเภท employee เท่านั้น ลูกค้า (customer) พาร์ทเนอร์ (partner) หรือประเภทอื่นจะได้รับบทบาทที่มีสิทธิ์ไม่ได้ และถ้าผู้ใช้มีบทบาทคอนโซลอยู่ จะเปลี่ยนประเภทผู้ใช้ไม่ได้จนกว่าจะเอาบทบาทออกก่อน",
          "ถ้าแอปขอ scope roles รายชื่อบทบาทของผู้ใช้จะถูกส่งไปใน token ด้วย บางแอปจึงอาจใช้บทบาท (เช่น staff) เป็นข้อมูลประกอบ",
        ],
      },
      {
        id: "built-in",
        heading: "บทบาทที่มีในระบบ",
        body: [
          'บทบาทที่มีป้าย "มีในระบบ" สร้างมาพร้อมระบบ แก้ไขหรือลบไม่ได้ (คอลัมน์ปุ่มแสดง "อ่านอย่างเดียว")',
        ],
        fields: [
          {
            name: "owner",
            description:
              "สิทธิ์ทุกข้อ รวมถึงการนำเข้าข้อมูล (data:import) · เฉพาะ owner เท่านั้นที่มอบหรือถอนบทบาท owner ได้ และ owner ถอนบทบาท owner ของตัวเองไม่ได้",
          },
          { name: "admin", description: "สิทธิ์ทุกข้อ ยกเว้นการนำเข้าข้อมูล (data:import)" },
          {
            name: "auditor",
            description:
              "ดูได้อย่างเดียวทุกส่วน (สิทธิ์ที่ลงท้ายด้วย :read ทั้งหมด รวมถึงบันทึกการใช้งาน) เหมาะกับผู้ตรวจสอบ",
          },
          {
            name: "staff",
            description: "พนักงานทั่วไป ไม่มีสิทธิ์ใด ๆ จึงเข้าคอนโซลไม่ได้ ใช้เป็นป้ายกำกับเท่านั้น",
          },
        ],
        fieldsTitle: "บทบาทที่มีในระบบ",
      },
      {
        id: "permissions",
        heading: "รายการสิทธิ์",
        body: [
          "สิทธิ์แบ่งเป็นหมวดตามส่วนของคอนโซล ในหน้าต่างสร้าง/แก้ไขบทบาท สิทธิ์จะแสดงเป็นกลุ่มตามหมวดเหล่านี้",
          'สิทธิ์ประเภทเขียน ลบ หรือนำเข้า (ทุกข้อยกเว้น :read และ data:export) นับว่าเป็น "ผู้ดูแลระบบ" ตามนโยบาย MFA "บังคับสำหรับผู้ดูแลระบบ" ผู้ที่มีสิทธิ์เหล่านี้จึงอาจถูกบังคับให้ตั้ง MFA',
        ],
        fieldsTitle: "สิทธิ์แต่ละข้อ",
        fields: [
          { name: "users:read", description: "ผู้ใช้ — ดูรายการและรายละเอียดผู้ใช้ ภาพรวม" },
          {
            name: "users:write",
            description:
              "ผู้ใช้ — เพิ่มผู้ใช้ แก้ไขโปรไฟล์ อีเมล/เบอร์โทร ระงับบัญชี ตั้งรหัสผ่าน รีเซ็ต MFA ฯลฯ",
          },
          { name: "users:delete", description: "ผู้ใช้ — ลบบัญชีผู้ใช้" },
          { name: "roles:read", description: "บทบาท — ดูหน้าบทบาท" },
          { name: "roles:write", description: "บทบาท — สร้าง แก้ไข ลบบทบาท และมอบบทบาทให้ผู้ใช้" },
          { name: "groups:read", description: "กลุ่ม — ดูกลุ่มและสมาชิก" },
          { name: "groups:write", description: "กลุ่ม — สร้าง แก้ไข ลบกลุ่ม และจัดการสมาชิก" },
          { name: "logs:read", description: "บันทึกการใช้งาน — ดูบันทึกการใช้งาน (audit log)" },
          {
            name: "settings:read",
            description:
              "การตั้งค่า — ดูวิธีเข้าสู่ระบบ ฟิลด์เพิ่มเติม ประเภทผู้ใช้ แบรนด์ ระยะเวลาเก็บบันทึก",
          },
          { name: "settings:write", description: "การตั้งค่า — แก้ไขการตั้งค่าเหล่านั้น" },
          { name: "clients:read", description: "OAuth clients — ดูรายการแอปและรายละเอียด" },
          {
            name: "clients:write",
            description: "OAuth clients — ลงทะเบียน แก้ไข ปิดใช้งาน ลบแอป และเปลี่ยน client secret",
          },
          { name: "data:export", description: "ข้อมูล — ส่งออกข้อมูลทั้งระบบ" },
          { name: "data:import", description: "ข้อมูล — นำเข้าข้อมูล (มีเฉพาะ owner โดยค่าเริ่มต้น)" },
        ],
      },
      {
        id: "list",
        heading: "ตารางบทบาท",
        fieldsTitle: "คอลัมน์ในตาราง",
        fields: [
          {
            name: "ชื่อ",
            description: 'ชื่อบทบาท ป้าย "มีในระบบ" (ถ้าเป็นบทบาทของระบบ) และคำอธิบายตัวเล็กด้านล่าง',
          },
          {
            name: "สิทธิ์",
            description:
              'สิทธิ์ทั้งหมดของบทบาท แสดงเป็นป้ายสีฟ้า ("ไม่มี" ถ้าไม่มีสิทธิ์) กรองได้ เช่น หาว่าบทบาทใดมี settings:write',
          },
          {
            name: "มีในระบบ",
            description: "บอกว่าเป็นบทบาทของระบบหรือไม่ (ซ่อนไว้โดยค่าเริ่มต้น)",
          },
          {
            name: 'ปุ่ม "แก้ไข" / "ลบ"',
            description:
              'มีเฉพาะบทบาทที่สร้างเองและเฉพาะผู้มีสิทธิ์ roles:write · บทบาทของระบบแสดง "อ่านอย่างเดียว"',
          },
        ],
      },
      {
        id: "create",
        heading: "สร้างบทบาท",
        shot: { file: "roles-create-dialog.png", caption: "หน้าต่างสร้างบทบาท พร้อมสิทธิ์ที่แบ่งตามหมวด" },
        fieldsTitle: "ช่องในหน้าต่าง",
        fields: [
          {
            name: "ชื่อ",
            required: true,
            description:
              "ใช้เป็นรหัสของบทบาท ตัวพิมพ์เล็ก ตัวเลข - หรือ _ ขึ้นต้นด้วยตัวอักษร (2–41 ตัวอักษร) เช่น helpdesk, hr_viewer ตัวพิมพ์ใหญ่จะถูกแปลงเป็นตัวพิมพ์เล็กให้ เปลี่ยนภายหลังไม่ได้ และต้องไม่ซ้ำกับบทบาทที่มีอยู่",
          },
          { name: "คำอธิบาย", description: "ไม่บังคับ อธิบายว่าบทบาทนี้มีไว้ให้ใคร (ไม่เกิน 500 ตัวอักษร)" },
          {
            name: "สิทธิ์",
            description:
              "ติ๊กสิทธิ์ที่ต้องการ แบ่งเป็นหมวด ผู้ใช้ บทบาท กลุ่ม บันทึกการใช้งาน การตั้งค่า OAuth clients ข้อมูล",
          },
        ],
        steps: [
          'กด "สร้างบทบาท" ที่มุมขวาบนของตาราง',
          "กรอกชื่อและคำอธิบาย",
          "ติ๊กสิทธิ์ที่ต้องการ — ให้เท่าที่จำเป็นกับงานเท่านั้น",
          'กด "บันทึก" — ระบบแสดง "บันทึกแล้ว" และบทบาทใหม่ปรากฏในตาราง',
          "ไปที่หน้ารายละเอียดผู้ใช้ → แท็บ บทบาท เพื่อมอบบทบาทนี้ให้พนักงาน",
        ],
        tips: [
          "ตัวอย่าง: บทบาท helpdesk สำหรับทีมซัพพอร์ตที่ช่วยผู้ใช้รีเซ็ตรหัสผ่าน ให้ users:read และ users:write เท่านั้น",
          "ตัวอย่าง: บทบาท group-manager สำหรับหัวหน้าทีมที่จัดการสมาชิกกลุ่ม ให้ users:read, groups:read และ groups:write",
        ],
      },
      {
        id: "edit",
        heading: "แก้ไขบทบาท",
        steps: [
          'กด "แก้ไข" ในแถวของบทบาทที่สร้างเอง',
          'หน้าต่าง "แก้ไขบทบาท <ชื่อ>" เปิดขึ้น ชื่อแก้ไม่ได้ แก้ได้เฉพาะคำอธิบายและสิทธิ์',
          'กด "บันทึก"',
        ],
        warnings: [
          "การแก้สิทธิ์มีผลกับทุกคนที่มีบทบาทนี้ทันที (ในการเรียกใช้คอนโซลครั้งถัดไป) การเพิ่มสิทธิ์ประเภทเขียนอาจทำให้ผู้ถือบทบาทถูกบังคับตั้ง MFA",
          "ถ้าบทบาทนี้เดิมไม่มีสิทธิ์ (เช่นใช้เป็นป้ายกำกับ) และมีผู้ถือที่ไม่ใช่ employee การเพิ่มสิทธิ์จะถูกปฏิเสธ เพราะบทบาทคอนโซลมีได้เฉพาะ employee",
        ],
      },
      {
        id: "delete",
        heading: "ลบบทบาท",
        shot: { file: "roles-delete-dialog.png", caption: "หน้าต่างยืนยันการลบบทบาท" },
        steps: [
          'กด "ลบ" ในแถวของบทบาทที่สร้างเอง',
          'หน้าต่างถาม "ลบบทบาท <ชื่อ> หรือไม่"',
          'กด "ลบ" เพื่อยืนยัน',
        ],
        warnings: [
          "ลบไม่ได้ถ้ายังมีผู้ใช้ถือบทบาทนี้อยู่ — ระบบแสดงข้อความผิดพลาดในหน้าต่าง ให้เอาบทบาทออกจากผู้ใช้ทุกคนก่อน (หน้าผู้ใช้ กรองตามบทบาทได้)",
          "การลบย้อนกลับไม่ได้ ถ้าต้องการบทบาทเดิมอีกต้องสร้างใหม่",
        ],
      },
    ],
    related: ["user-detail", "users", "user-types", "sign-in-method", "audit-logs"],
  },
  en: {
    id: "roles",
    title: "Roles",
    summary:
      "Roles decide what each administrator may do in this console. A role is a set of permissions (such as viewing users, changing settings, reading the audit log). This page lists all roles and lets you create, edit and delete custom roles.",
    access:
      "Viewing needs roles:read (owner, admin, auditor). Creating, editing and deleting roles needs roles:write (owner, admin). Roles are given to people on the user's page → Roles tab (needs roles:write).",
    shot: { file: "roles-list.png", caption: "The roles list: the four built-in roles and a custom role" },
    sections: [
      {
        id: "concept",
        heading: "What roles are and who can hold them",
        body: [
          "Roles apply to this admin console only; they do not decide whether someone can sign in to other applications (user types and groups do that). A person can hold several roles and gets the combined permissions.",
          "Only people with a role that has at least one permission can open the console. People without roles, or only with roles without permissions (such as staff), cannot.",
          "Console roles can only be given to accounts of type employee. Customers, partners and other types cannot receive a role with permissions, and a user who holds a console role cannot have their user type changed until the roles are removed.",
          "When an application asks for the roles scope, the user's role names are also sent in tokens, so some applications may use roles (e.g. staff) as extra information.",
        ],
      },
      {
        id: "built-in",
        heading: "Built-in roles",
        body: [
          'Roles with the "Built-in" badge come with the system and cannot be edited or deleted (the actions column shows "Read only").',
        ],
        fieldsTitle: "Built-in roles",
        fields: [
          {
            name: "owner",
            description:
              "Every permission, including data import (data:import). Only owners can give or take away the owner role, and owners cannot remove their own owner role.",
          },
          { name: "admin", description: "Every permission except data import (data:import)." },
          {
            name: "auditor",
            description:
              "Read-only access everywhere (all :read permissions, including the audit log). Suits auditors.",
          },
          {
            name: "staff",
            description: "Regular employee with no permissions, so no console access. A label only.",
          },
        ],
      },
      {
        id: "permissions",
        heading: "The permissions",
        body: [
          "Permissions are grouped by console area. The create/edit window shows them grouped the same way.",
          'Write, delete and import permissions (everything except :read and data:export) count as "administrator" for the MFA policy "Required for administrators", so holders may be forced to set up MFA.',
        ],
        fieldsTitle: "Each permission",
        fields: [
          { name: "users:read", description: "Users — view the user list, user pages and the overview." },
          {
            name: "users:write",
            description:
              "Users — add users, edit profiles, emails/phones, ban accounts, set passwords, reset MFA, etc.",
          },
          { name: "users:delete", description: "Users — delete user accounts." },
          { name: "roles:read", description: "Roles — view the Roles page." },
          { name: "roles:write", description: "Roles — create, edit, delete roles and give roles to users." },
          { name: "groups:read", description: "Groups — view groups and members." },
          { name: "groups:write", description: "Groups — create, edit, delete groups and manage members." },
          { name: "logs:read", description: "Audit logs — view the audit log." },
          {
            name: "settings:read",
            description:
              "Settings — view Sign-in method, Custom fields, User types, Branding, Log retention.",
          },
          { name: "settings:write", description: "Settings — change those settings." },
          { name: "clients:read", description: "OAuth clients — view applications and their details." },
          {
            name: "clients:write",
            description: "OAuth clients — register, edit, disable, delete applications and rotate secrets.",
          },
          { name: "data:export", description: "Data — export the whole system's data." },
          { name: "data:import", description: "Data — import data (owner only by default)." },
        ],
      },
      {
        id: "list",
        heading: "The roles table",
        fieldsTitle: "Columns in the table",
        fields: [
          {
            name: "Name",
            description:
              'The role name, a "Built-in" badge for system roles, and the description in small text underneath.',
          },
          {
            name: "Permissions",
            description:
              'All the role\'s permissions as blue badges ("None" if it has none). Filterable — e.g. find which roles have settings:write.',
          },
          { name: "Built-in", description: "Whether it is a system role (hidden by default)." },
          {
            name: '"Edit" / "Delete" buttons',
            description: 'Only for custom roles and only with roles:write. Built-in roles show "Read only".',
          },
        ],
      },
      {
        id: "create",
        heading: "Create a role",
        shot: {
          file: "roles-create-dialog.png",
          caption: "The Create role window with permissions grouped by area",
        },
        fieldsTitle: "Fields in the window",
        fields: [
          {
            name: "Name",
            required: true,
            description:
              "Also the role's ID: lowercase letters, digits, - or _, starting with a letter (2–41 characters), e.g. helpdesk or hr_viewer. Capitals are lower-cased automatically. It cannot be changed later and must be unique.",
          },
          { name: "Description", description: "Optional: who the role is for (up to 500 characters)." },
          {
            name: "Permissions",
            description:
              "Tick the permissions to grant, grouped as Users, Roles, Groups, Audit logs, Settings, OAuth clients, Data.",
          },
        ],
        steps: [
          'Click "Create role" at the top right of the table.',
          "Enter the name and a description.",
          "Tick the permissions — only what the job needs.",
          'Click "Save" — "Saved" appears and the new role is in the table.',
          "Open a user's page → Roles tab to give the role to an employee.",
        ],
        tips: [
          "Example: a helpdesk role for support staff who help users reset passwords — users:read and users:write only.",
          "Example: a group-manager role for team leads who manage group members — users:read, groups:read and groups:write.",
        ],
      },
      {
        id: "edit",
        heading: "Edit a role",
        steps: [
          'Click "Edit" in a custom role\'s row.',
          'The "Edit role <name>" window opens. The name cannot be changed; only the description and permissions can.',
          'Click "Save".',
        ],
        warnings: [
          "Permission changes apply to everyone holding the role right away (on their next console request). Adding write permissions may force holders to set up MFA.",
          "If the role had no permissions (used as a label) and is held by someone who is not an employee, adding permissions is refused, because console roles are for employees only.",
        ],
      },
      {
        id: "delete",
        heading: "Delete a role",
        shot: { file: "roles-delete-dialog.png", caption: "The delete-role confirmation" },
        steps: [
          'Click "Delete" in a custom role\'s row.',
          'The window asks "Delete role <name>?".',
          'Click "Delete" to confirm.',
        ],
        warnings: [
          "A role that is still held by any user cannot be deleted — an error appears in the window. Remove it from every user first (the Users page can filter by role).",
          "Deleting cannot be undone; to get the role back you must create it again.",
        ],
      },
    ],
    related: ["user-detail", "users", "user-types", "sign-in-method", "audit-logs"],
  },
};

export default topic;
